import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AuthHero from '../../../shared/auth/components/AuthHero';
import FlowGuard from '../../../shared/components/FlowGuard';

interface RecruiterWelcomeContentProps {
    companyName: string;
}

const RecruiterWelcomeContent: React.FC<RecruiterWelcomeContentProps> = ({ companyName }) => {
    const navigate = useNavigate();

    const handleGoToDashboard = () => {
        // Navigate to company dashboard (recruiters use company dashboard)
        navigate('/companies/dashboard');
    };

    return (
        <div className="min-h-screen bg-[#F8F9FB] flex flex-col lg:flex-row">
            {/* Left Side - Welcome Message */}
            <div className="flex-1 flex items-center justify-center p-4 sm:p-8">
                <div className="w-full max-w-[745px] text-center">
                    {/* Logo */}
                    <div className="mb-6 sm:mb-8">
                        <img
                            src="/branding/logo_blue.svg"
                            alt="Hush"
                            className="h-10 sm:h-12 w-auto mx-auto"
                        />
                    </div>

                    {/* Title */}
                    <h1
                        className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#015FA6] mb-4 sm:mb-6"
                        style={{
                            fontFamily: 'Manrope',
                            lineHeight: '100%'
                        }}
                    >
                        Welcome to {companyName} on Hush
                    </h1>

                    {/* Subtitle */}
                    <p
                        className="text-base sm:text-lg text-[#015FA6] mb-8 sm:mb-12 lg:mb-[72px]"
                        style={{
                            fontFamily: 'Manrope',
                            fontWeight: 300,
                            lineHeight: '150%'
                        }}
                    >
                        Your account is created and your workspace is all set up.
                    </p>

                    {/* Success Icon */}
                    <div
                        className="w-48 h-48 sm:w-56 sm:h-56 lg:w-[272px] lg:h-[272px] mx-auto mb-8 sm:mb-12 lg:mb-[72px] relative flex items-center justify-center"
                    >
                        {/* Outer Circle */}
                        <div
                            style={{
                                width: '272px',
                                height: '272px',
                                background: '#015FA6',
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                filter: 'drop-shadow(0px 60px 100px rgba(16, 45, 97, 0.08))'
                            }}
                        >
                            {/* Inner Badge */}
                            <div
                                style={{
                                    width: '218px',
                                    height: '218px',
                                    background: 'linear-gradient(135deg, #4A90E2 0%, #2563EB 100%)',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    position: 'relative'
                                }}
                            >
                                {/* Badge Shape */}
                                <div
                                    style={{
                                        width: '180px',
                                        height: '180px',
                                        background: 'rgba(255, 255, 255, 0.15)',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        position: 'relative'
                                    }}
                                >
                                    {/* Star-like badge background */}
                                    <svg width="160" height="160" viewBox="0 0 160 160" style={{ position: 'absolute' }}>
                                        <path
                                            d="M80,20 L95,60 L140,60 L105,90 L120,130 L80,105 L40,130 L55,90 L20,60 L65,60 Z"
                                            fill="rgba(255, 255, 255, 0.1)"
                                        />
                                    </svg>

                                    {/* Checkmark */}
                                    <svg width="90" height="90" viewBox="0 0 90 90" fill="none" style={{ position: 'relative', zIndex: 1 }}>
                                        <path
                                            d="M25 45L38 58L65 31"
                                            stroke="#FFFFFF"
                                            strokeWidth="8"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Go to Dashboard Button */}
                    <button
                        onClick={handleGoToDashboard}
                        className="w-full max-w-[529px] h-12 sm:h-[55px] px-6 bg-[#015FA6] hover:bg-[#014580] text-white text-base sm:text-xl font-semibold rounded-lg transition-colors inline-flex items-center justify-center gap-2"
                        style={{
                            fontFamily: 'Manrope'
                        }}
                    >
                        Go to Dashboard
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                            <path
                                d="M5 12h14m-7-7l7 7-7 7"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </button>
                </div>
            </div>

            {/* Right Side - Hero */}
            <AuthHero variant="recruiter" />
        </div>
    );
};

/**
 * RecruiterWelcome Page
 * Protected by FlowGuard - only accessible after recruiter setup flow
 * Direct URL access redirects to login page
 */
const RecruiterWelcome: React.FC = () => {
    const location = useLocation();

    // Get user info from navigation state or localStorage
    const companyName = location.state?.companyName || 'Hush Company';

    return (
        <FlowGuard
            requiredStateKey="fromRecruiterSetup"
            fallbackPath="/auth/login"
        >
            <RecruiterWelcomeContent companyName={companyName} />
        </FlowGuard>
    );
};

export default RecruiterWelcome;
